import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  MdFeedback, MdSettings, MdSearch, MdEdit, MdLocationOn, MdNote, MdOutlinePayment
} from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { FiChevronDown, FiBox } from "react-icons/fi";
import { FaUserCircle } from "react-icons/fa";
import { AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import "./SuppliersPage.css";
import { useCurrency } from '../currency/currencyContext'; // Add currency context

const SuppliersPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    totalOpenBalance: "0.00",
    totalOverdue: "0.00",
    totalPaid: "0.00",
    supplierBalances: {},
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [selectedSupplierName, setSelectedSupplierName] = useState("");
  const formRef = useRef(null);
  const deleteModalRef = useRef(null);
  const API_BASE_URL = "https://backend.hira-tech.co.uk";
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency(); // Add currency context

  // Fetch suppliers and their transactions
  useEffect(() => {
    // Fetch suppliers
    fetch(`${API_BASE_URL}/api/suppliers/`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch suppliers: ${response.statusText}`);
        return response.json();
      })
      .then((data) => {
        console.log("Suppliers:", data);
        setSuppliers(data);
      })
      .catch((error) => console.error("Error fetching suppliers:", error));

    // Fetch transactions for all suppliers
    fetch(`${API_BASE_URL}/api/suppliers/`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch suppliers: ${response.statusText}`);
        return response.json();
      })
      .then(async (suppliersData) => {
        const allTransactions = [];
        for (const supplier of suppliersData) {
          // Fetch bills
          const billsResponse = await fetch(`${API_BASE_URL}/api/suppliers/${supplier.id}/bills/`);
          if (!billsResponse.ok) {
            console.error(`Failed to fetch bills for supplier ${supplier.id}: ${billsResponse.statusText}`);
            continue;
          }
          const bills = await billsResponse.json();
          console.log(`Bills API response for supplier ${supplier.id}:`, bills);
          const mappedBills = bills.map((bill) => ({
            id: bill.id,
            date: bill.date || "",
            paid_date: bill.paid_date || "",
            type: "Bill",
            no: bill.bill_number || "",
            payee: supplier.supplier_display_name || `${supplier.first_name} ${supplier.last_name}`,
            category: bill.terms || "General",
            terms: bill.terms || "General",
            totalBeforeTax: bill.amount ? (parseFloat(bill.amount) / 1.17).toFixed(2) : "0.00",
            salesTax: bill.amount ? (parseFloat(bill.amount) * 0.17 / 1.17).toFixed(2) : "0.00",
            total: parseFloat(bill.amount || bill.total || 0).toFixed(2),
            status: bill.status || (bill.paid_date ? "Paid" : "Pending"),
            supplier: bill.supplier || supplier.id,
            due_date: bill.due_date || "",
          }));

          // Fetch expenses
          const expensesResponse = await fetch(`${API_BASE_URL}/api/suppliers/${supplier.id}/expenses/`);
          if (!expensesResponse.ok) {
            console.error(`Failed to fetch expenses for supplier ${supplier.id}: ${expensesResponse.statusText}`);
            continue;
          }
          const expenses = await expensesResponse.json();
          const mappedExpenses = expenses.map((expense) => ({
            id: expense.id,
            date: expense.date || "",
            type: "Expense",
            no: expense.no || expense.expense_number || `EXP-${expense.id}`,
            payee: supplier.supplier_display_name || `${supplier.first_name} ${supplier.last_name}`,
            category: expense.terms || "General",
            terms: expense.terms || "General",
            totalBeforeTax: expense.amount ? (parseFloat(expense.amount) / 1.17).toFixed(2) : "0.00",
            salesTax: expense.amount ? (parseFloat(expense.amount) * 0.17 / 1.17).toFixed(2) : "0.00",
            total: parseFloat(expense.amount || expense.total || 0).toFixed(2),
            status: expense.status || "Pending",
            supplier: expense.supplier || supplier.id,
            due_date: expense.due_date || "",
          }));

          allTransactions.push(...mappedBills, ...mappedExpenses);
        }
        console.log("All Transactions:", allTransactions);
        setTransactions(allTransactions);
      })
      .catch((error) => console.error("Error fetching transactions:", error));
  }, []);

  useEffect(() => {
    let totalOpenBalance = 0;
    let totalOverdue = 0;
    let totalPaid = 0;
    const supplierBalances = {};

    const currentMonthStart = new Date();
    currentMonthStart.setDate(1);

    const paidTransactions = transactions.filter(t => t.status === "Paid");
    console.log("Paid transactions for totalPaid:", paidTransactions);

    transactions.forEach((transaction) => {
      const supplierId = typeof transaction.supplier === "object" ? transaction.supplier.id : transaction.supplier;
      const amount = parseFloat(transaction.total || 0);

      if (!supplierBalances[supplierId]) {
        supplierBalances[supplierId] = { openBalance: 0, overdue: 0 };
      }

      if (transaction.type === "Bill" && ["Pending", "Unpaid"].includes(transaction.status)) {
        supplierBalances[supplierId].openBalance += amount;
        totalOpenBalance += amount;

        const dueDate = transaction.due_date ? new Date(transaction.due_date) : null;
        if (dueDate && dueDate < currentMonthStart) {
          supplierBalances[supplierId].overdue += amount;
          totalOverdue += amount;
        }
      }

      if (transaction.status === "Paid") {
        totalPaid += amount;
      }
    });

    setStats({
      totalOpenBalance: totalOpenBalance.toFixed(2),
      totalOverdue: totalOverdue.toFixed(2),
      totalPaid: totalPaid.toFixed(2),
      supplierBalances,
    });
    console.log("Stats:", { totalOpenBalance, totalOverdue, totalPaid, supplierBalances });
  }, [transactions]);

  useEffect(() => {
    console.log("Selected Suppliers:", selectedSuppliers);
    console.log("Select All:", selectAll);
  }, [selectedSuppliers, selectAll]);

  const handleOutsideClick = (e) => {
    if (formRef.current && !formRef.current.contains(e.target)) {
      setShowForm(false);
      setEditingSupplier(null);
    }
  };

  useEffect(() => {
    const handleOutsideDeleteClick = (e) => {
      if (deleteModalRef.current && !deleteModalRef.current.contains(e.target)) {
        setIsDeleteModalOpen(false);
      }
    };
    if (isDeleteModalOpen) {
      document.addEventListener("mousedown", handleOutsideDeleteClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideDeleteClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideDeleteClick);
  }, [isDeleteModalOpen]);

  useEffect(() => {
    if (showForm) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showForm]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const supplier_display_name = formData.get("supplier_display_name")?.trim();

    // Validate that supplier_display_name is provided
    if (!supplier_display_name) {
      alert("Supplier Display Name is required.");
      return;
    }

    const supplierData = {
      title: formData.get("title")?.trim() || "",
      first_name: formData.get("first_name")?.trim() || "",
      middle_name: formData.get("middle_name")?.trim() || "",
      last_name: formData.get("last_name")?.trim() || "",
      suffix: formData.get("suffix")?.trim() || "",
      company_name: formData.get("company_name")?.trim() || "",
      supplier_display_name,
      email: formData.get("email")?.trim() || "",
      phone_number: formData.get("phone_number")?.trim() || "",
      mobile_number: formData.get("mobile_number")?.trim() || "",
      fax: formData.get("fax")?.trim() || "",
      other: formData.get("other")?.trim() || "",
      website: formData.get("website")?.trim() || "",
      street_address: formData.get("street_address")?.trim() || "",
      city: formData.get("city")?.trim() || "",
      country: formData.get("country")?.trim() || "",
      postcode: formData.get("postcode")?.trim() || "",
      state: formData.get("state")?.trim() || "",
      balance: parseFloat(formData.get("balance")) || 0.00,
    };

    const url = editingSupplier
      ? `${API_BASE_URL}/api/suppliers/${editingSupplier.id}/`
      : `${API_BASE_URL}/api/suppliers/`;
    const method = editingSupplier ? "PATCH" : "POST";

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(supplierData),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            throw new Error(JSON.stringify(errorData));
          });
        }
        return response.json();
      })
      .then((data) => {
        if (editingSupplier) {
          setSuppliers(
            suppliers.map((s) => (s.id === editingSupplier.id ? data : s))
          );
          alert("Supplier updated successfully!");
        } else {
          setSuppliers([...suppliers, data]);
          alert("Supplier added successfully!");
        }
        setShowForm(false);
        setEditingSupplier(null);
      })
      .catch((error) => {
        console.error("Error processing supplier:", error);
        alert(`Failed to ${editingSupplier ? "update" : "add"} supplier: ${error.message}`);
      });
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier({
      ...supplier,
      balance: supplier.balance || "0.00",
    });
    setShowForm(true);
  };

  const handleDeleteSupplier = async () => {
    if (!selectedSupplierId) return;

    try {
      const supplierTransactions = transactions.filter((t) => t.supplier === selectedSupplierId);
      for (const transaction of supplierTransactions) {
        const endpoint = transaction.type === "Bill"
          ? `${API_BASE_URL}/api/bills/${transaction.id}/`
          : `${API_BASE_URL}/api/expenses/${transaction.id}/`;
        const response = await fetch(endpoint, {
          method: "DELETE",
        });
        if (!response.ok) {
          throw new Error(`Failed to delete ${transaction.type.toLowerCase()} ID ${transaction.id}`);
        }
      }

      const supplierResponse = await fetch(`${API_BASE_URL}/api/suppliers/${selectedSupplierId}/`, {
        method: "DELETE",
      });
      if (!supplierResponse.ok) {
        throw new Error("Failed to delete supplier");
      }

      setSuppliers(suppliers.filter((s) => s.id !== selectedSupplierId));
      setTransactions(transactions.filter((t) => t.supplier !== selectedSupplierId));
      setIsDeleteModalOpen(false);
      alert("Supplier and associated transactions deleted successfully!");
    } catch (error) {
      console.error("Error deleting supplier or transactions:", error);
      alert("Failed to delete supplier or transactions: " + error.message);
    }
  };

  const handleMakePayment = async (supplierId, billId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bills/${billId}/pay/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to mark bill as paid: ${errorData.message || response.statusText}`);
      }

      const updatedBill = await response.json();
      console.log("Payment API response in SuppliersPage:", updatedBill);
      const mappedBill = {
        id: updatedBill.id,
        date: updatedBill.date || "",
        paid_date: updatedBill.paid_date || new Date().toISOString().split("T")[0],
        type: "Bill",
        no: updatedBill.bill_number || "",
        payee: updatedBill.supplier_display_name || updatedBill.supplier_name || "",
        category: updatedBill.terms || "General",
        terms: updatedBill.terms || "General",
        totalBeforeTax: updatedBill.amount ? (parseFloat(updatedBill.amount) / 1.17).toFixed(2) : "0.00",
        salesTax: updatedBill.amount ? (parseFloat(updatedBill.amount) * 0.17 / 1.17).toFixed(2) : "0.00",
        total: parseFloat(updatedBill.amount || updatedBill.total || 0).toFixed(2),
        status: updatedBill.status || (updatedBill.paid_date ? "Paid" : "Pending"),
        supplier: updatedBill.supplier || supplierId,
        due_date: updatedBill.due_date || "",
      };

      setTransactions((prev) => {
        const newTransactions = prev.map((t) => (t.id === billId && t.type === "Bill" ? mappedBill : t));
        console.log("Updated transactions after payment:", newTransactions);
        console.log("Stats after payment:", stats);
        return newTransactions;
      });
      alert("Bill marked as paid!");
    } catch (error) {
      console.error("Error marking bill as paid:", error);
      alert(`Failed to mark bill as paid: ${error.message}`);
    }
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectedSuppliers(checked ? filteredSuppliers.map((s) => s.id) : []);
  };

  const handleSelectSupplier = (e, supplierId) => {
    e.stopPropagation();
    e.preventDefault();
    const checked = e.target.checked;
    setSelectedSuppliers((prev) => {
      const newSelected = checked
        ? [...new Set([...prev, supplierId])]
        : prev.filter((id) => id !== supplierId);
      setSelectAll(newSelected.length === filteredSuppliers.length);
      return newSelected;
    });
  };

  const filteredSuppliers = suppliers.filter((s) => {
    const supplierName = s.supplier_display_name || `${s.first_name} ${s.last_name}`;
    return supplierName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="suppliers-container">
      {showForm && (
        <div className="side-form-overlay">
          <div className="side-form right show" ref={formRef}>
            <button className="close-button" onClick={() => { setShowForm(false); setEditingSupplier(null); }}>×</button>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, marginTop: "20px" }}>{editingSupplier ? "Edit Supplier" : "New Supplier"}</h2>
              <div style={{ display: "flex", gap: "10px", fontSize: "20px", marginTop: "20px" }}>
                <MdSettings />
                <MdEdit />
                <FaUserCircle />
                <FiBox />
                <MdFeedback />
              </div>
            </div>
            <form
              onSubmit={handleFormSubmit}
              style={{
                border: "2px solid white",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                padding: "20px",
                marginTop: "10px",
              }}
            >
              <div className="form-section">
                <div className="section-header">
                  <MdNote className="section-icon" />
                  <h3>Names and Contacts</h3>
                </div>
                <div className="five-col-custom">
                  {["title", "first_name", "middle_name", "last_name", "suffix"].map((name, i) => (
                    <div key={i} className="form-groups">
                      <label>{name.replace("_", " ").toUpperCase()}</label>
                      <input
                        type="text"
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-grid two-col">
                  {["company_name", "supplier_display_name"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.replace("_", " ").toUpperCase()}</label>
                      <input
                        type="text"
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                        required={name === "supplier_display_name"}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-grid two-col">
                  {["email", "phone_number"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.replace("_", " ").toUpperCase()}</label>
                      <input
                        type={name === "email" ? "email" : "text"}
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-grid two-col">
                  {["mobile_number", "fax"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.replace("_", " ").toUpperCase()}</label>
                      <input
                        type="text"
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-grid two-col">
                  {["other", "website"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.replace("_", " ").toUpperCase()}</label>
                      <input
                        type={name === "website" ? "url" : "text"}
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-section">
                <div className="section-header">
                  <MdLocationOn className="section-icon" />
                  <h3>Address</h3>
                </div>
                <div className="form-group full-width">
                  <label>Street Address</label>
                  <textarea
                    rows="2"
                    name="street_address"
                    defaultValue={editingSupplier ? editingSupplier.street_address : ""}
                  ></textarea>
                </div>
                <div className="form-grid two-col">
                  {["city", "country"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.toUpperCase()}</label>
                      <input
                        type="text"
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-grid two-col">
                  {["postcode", "state"].map((name, i) => (
                    <div key={i} className="form-group">
                      <label>{name.toUpperCase()}</label>
                      <input
                        type="text"
                        name={name}
                        defaultValue={editingSupplier ? editingSupplier[name] : ""}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-group">
                  <label>BALANCE ({getCurrencySymbol()})</label>
                  <input
                    type="number"
                    step="0.01"
                    name="balance"
                    defaultValue={editingSupplier ? editingSupplier.balance : "0.00"}
                    placeholder={`Balance in ${getCurrencySymbol()}`}
                  />
                </div>
                <div className="form-buttons">
                  <button type="button" className="preview-btn">Preview Address</button>
                </div>
              </div>
              <div className="form-submit">
                <button type="submit">{editingSupplier ? "Update Supplier" : "Submit New Supplier"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <h1>Expenses</h1>
      <nav className="nav-bar">
        <NavLink to="/admin/pk-expenses" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Expenses</NavLink>
        <NavLink to="/admin/bills" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Bills</NavLink>
        <NavLink to="/admin/pk-suppliers" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Suppliers</NavLink>
      </nav>
      <div className="suppliers-header-row">
        <p className="suppliers-title">Suppliers</p>
        <div className="suppliers-buttons">
          <NavLink to="/admin/add-supplier" className="header-btn"><MdFeedback /> Give Feedback</NavLink>
          <button onClick={() => { setShowForm(true); setEditingSupplier(null); }} className="header-nav-btn">New Supplier</button>
          <NavLink to="/admin/export-suppliers" className="nav-btn icon-only"><FiChevronDown size={22} /></NavLink>
        </div>
      </div>
      <div className="info-boxes">
        <div className="info-box box-total-suppliers"><h4>{suppliers.length}</h4><p>Total Suppliers</p></div>
        <div className="info-box box-pending-verifications"><h4>{formatPrice(stats.totalOverdue)}</h4><p>Overdue</p></div>
        <div className="info-box box-recently-added"><h4>{formatPrice(stats.totalOpenBalance)}</h4><p>Open Balance</p></div>
        <div className="info-box box-inactive-suppliers"><h4>{formatPrice(stats.totalPaid)}</h4><p>Total Paid</p></div>
      </div>
      <div className="suppliers-table-controls">
        <div className="search-wrapper">
          <input
            className="supplier-search"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MdSearch className="search-icon" />
        </div>
        <div className="supplier-icons">
          <NavLink to="/admin/settings"><MdSettings size={22} color="black" /></NavLink>
          <NavLink to="/admin/profile"><FaUserCircle size={22} color="black" /></NavLink>
          <NavLink to="/admin/inventory"><FiBox size={22} color="black" /></NavLink>
        </div>
      </div>
      <div className="suppliers-table">
        <div className="table-row table-header">
          <div className="checkbox-col">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
            />
          </div>
          <div className="supplier-col">Supplier <AiOutlineArrowUp /></div>
          <div className="company-col">Company Name <AiOutlineArrowUp /><AiOutlineArrowDown /></div>
          <div className="phone-col">Phone</div>
          <div className="email-col">Email</div>
          <div className="balance-col">Open Balance <AiOutlineArrowUp /><AiOutlineArrowDown /></div>
          <div className="action">Actions</div>
        </div>
        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content" ref={deleteModalRef} style={{width:"400px"}}>
              <div className="modal-header">
                <h3>Delete Supplier</h3>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{selectedSupplierName}</strong>?</p>
                <p className="modal-label">This action cannot be undone. Associated bills and expenses will also be deleted.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-btn modal-cancel-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
                <button className="modal-btn modal-delete-btn" onClick={handleDeleteSupplier}>Delete</button>
              </div>
            </div>
          </div>
        )}
        {filteredSuppliers.map((s) => (
          <NavLink
            key={s.id}
            to={`/admin/supplier-details/${s.id}`}
            className="table-row"
            style={{ textDecoration: "none", color: "inherit" }}
            onClick={(e) => {
              if (
                e.target.tagName === "INPUT" ||
                e.target.tagName === "BUTTON" ||
                e.target.closest(".actions-btn") ||
                e.target.closest(".actions-col") ||
                e.target.closest(".modal-content")
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <div className="checkbox-col">
              <input
                type="checkbox"
                checked={selectedSuppliers.includes(s.id)}
                onChange={(e) => handleSelectSupplier(e, s.id)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="supplier-col">{s.supplier_display_name || `${s.first_name} ${s.last_name}`}</div>
            <div className="company-col">{s.company_name || "-"}</div>
            <div className="phone-col">{s.phone_number || "-"}</div>
            <div className="email-col">{s.email || "-"}</div>
            <div className="balance-col">{formatPrice(stats.supplierBalances[s.id]?.openBalance || 0)}</div>
            <div className="actions-col" style={{ display: "flex", height: "100px" }}>
              <button
                className="actions-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  const unpaidBills = transactions.filter(t => t.supplier === s.id && t.type === "Bill" && ["Pending", "Unpaid"].includes(t.status));
                  if (unpaidBills.length > 0) {
                    handleMakePayment(s.id, unpaidBills[0].id);
                  } else {
                    alert("No unpaid bills for this supplier.");
                  }
                }}
                title="Make Payment"
              >
                <MdOutlinePayment />
              </button>
              <button
                className="actions-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditSupplier(s);
                }}
                title="Edit Supplier"
              >
                <MdEdit style={{ color: "#4a90e2" }} />
              </button>
              <button
                className="actions-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedSupplierId(s.id);
                  setSelectedSupplierName(s.supplier_display_name || `${s.first_name} ${s.last_name}`);
                  setIsDeleteModalOpen(true);
                }}
                title="Delete Supplier"
              >
                <MdDelete style={{ color: "crimson" }} />
              </button>
            </div>
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default SuppliersPage;